/**
 * 默认录音实现（T10）：`navigator.mediaDevices.getUserMedia` + `MediaRecorder`。
 *
 * 录音能力通过 `Recorder` 接口注入，本文件只是「默认实现」；测试注入 fake，
 * 因此不会请求真实麦克风。构造器可注入 `mediaDevices` 与 `MediaRecorder` 构造器，
 * 以便在 jsdom 下用假设备验证录音 → 产出的音频 Blob。
 */

import type { Recorder } from './types';

export type MediaRecorderConstructor = new (
  stream: MediaStream,
  options?: MediaRecorderOptions,
) => MediaRecorder;

export interface MediaRecorderRecorderOptions {
  /** 覆盖 `navigator.mediaDevices`（测试用假设备）。 */
  mediaDevices?: Pick<MediaDevices, 'getUserMedia'>;
  /** 覆盖 `MediaRecorder` 构造器（jsdom 无此全局）。 */
  mediaRecorderCtor?: MediaRecorderConstructor;
  /** 指定录制 MIME（如 `audio/webm`）；缺省交给浏览器选择。 */
  mimeType?: string;
}

interface PendingStop {
  resolve: (blob: Blob) => void;
  reject: (error: Error) => void;
}

/** 基于 MediaRecorder 的默认录音器。 */
export class MediaRecorderRecorder implements Recorder {
  private readonly mediaDevices: Pick<MediaDevices, 'getUserMedia'>;
  private readonly ctor: MediaRecorderConstructor;
  private readonly mimeType: string | undefined;

  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private pending: PendingStop | null = null;

  constructor(
    mediaDevices: Pick<MediaDevices, 'getUserMedia'>,
    ctor: MediaRecorderConstructor,
    mimeType?: string,
  ) {
    this.mediaDevices = mediaDevices;
    this.ctor = ctor;
    this.mimeType = mimeType;
  }

  /** 是否正在录音（供 UI 判断，非接口要求）。 */
  get recording(): boolean {
    return this.recorder !== null;
  }

  async start(): Promise<void> {
    if (this.recorder !== null) throw new Error('录音已在进行中');

    const stream = await this.mediaDevices.getUserMedia({ audio: true });
    const recorder = new this.ctor(
      stream,
      this.mimeType === undefined ? undefined : { mimeType: this.mimeType },
    );
    this.stream = stream;
    this.recorder = recorder;
    this.chunks = [];

    recorder.addEventListener('dataavailable', (event: BlobEvent) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    });
    recorder.addEventListener('stop', () => {
      const blob = new Blob(this.chunks, this.mimeType === undefined ? {} : { type: this.mimeType });
      const pending = this.pending;
      this.pending = null;
      this.chunks = [];
      this.recorder = null;
      this.releaseStream();
      pending?.resolve(blob);
    });
    recorder.addEventListener('error', () => {
      const pending = this.pending;
      this.pending = null;
      this.chunks = [];
      this.recorder = null;
      this.releaseStream();
      pending?.reject(new Error('录音设备出错'));
    });

    recorder.start();
  }

  stop(): Promise<Blob> {
    const recorder = this.recorder;
    if (recorder === null) return Promise.reject(new Error('当前没有进行中的录音'));
    return new Promise<Blob>((resolve, reject) => {
      this.pending = { resolve, reject };
      recorder.stop();
    });
  }

  cancel(): void {
    const recorder = this.recorder;
    this.chunks = [];
    this.pending = null;
    this.recorder = null;
    try {
      recorder?.stop();
    } catch {
      // 已停止时忽略
    }
    this.releaseStream();
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}

function readGlobalMediaDevices(): Pick<MediaDevices, 'getUserMedia'> | null {
  try {
    const devices = globalThis.navigator?.mediaDevices;
    if (devices === undefined || typeof devices.getUserMedia !== 'function') return null;
    return devices;
  } catch {
    return null;
  }
}

function readGlobalMediaRecorder(): MediaRecorderConstructor | null {
  const globalCtor: unknown = globalThis.MediaRecorder;
  return typeof globalCtor === 'function' ? (globalCtor as MediaRecorderConstructor) : null;
}

/**
 * 构造默认录音器；环境不支持（无 mediaDevices / MediaRecorder）时返回 `null`，
 * 由上层给出结构化「不支持录音」提示，而不是抛错白屏。
 */
export function createMediaRecorderRecorder(
  options: MediaRecorderRecorderOptions = {},
): Recorder | null {
  const devices = options.mediaDevices ?? readGlobalMediaDevices();
  const ctor = options.mediaRecorderCtor ?? readGlobalMediaRecorder();
  if (devices === null || ctor === null) return null;
  return new MediaRecorderRecorder(devices, ctor, options.mimeType);
}